import React, { useEffect, useRef } from "react";
import type { ApiMcFanChart } from "@tradingdroid/frontends-types";
import type * as T from "@src/types";
import { ColorType, CrosshairMode, LineSeries, LineStyle, createChart } from "lightweight-charts";

interface Props {
  fanChart: ApiMcFanChart;
  initialCapital: number;
  height?: number;
}

// Outer percentiles are dimmer than the median, so the eye lands on the middle of the fan first.
const BAND_STYLE: Record<string, { color: string; width: 1 | 2 | 3 }> = {
  p5: { color: "rgba(244, 67, 54, 0.55)", width: 1 },
  p25: { color: "rgba(255, 167, 38, 0.65)", width: 1 },
  p50: { color: "rgba(255, 255, 255, 0.9)", width: 2 },
  p75: { color: "rgba(102, 187, 106, 0.65)", width: 1 },
  p95: { color: "rgba(76, 175, 80, 0.55)", width: 1 },
};

const FALLBACK_BAND = { color: "rgba(158, 158, 158, 0.6)", width: 1 as const };

interface SeriesInfo {
  series: T.TVL.ISeriesApi<"Line">;
  label: string;
  color: string;
}

/**
 * Percentile bands of the simulated equity paths, drawn as one line per percentile.
 *
 * The x axis is a trade index, not a date. lightweight-charts only accepts a time scale, so the
 * index is fed through as a whole-second timestamp and the axis labels are formatted back into
 * trade numbers. Without that the axis would read as January 1970. The y axis is equity in quote
 * currency; a crosshair tooltip spells out each band's value at the hovered trade.
 */
export const McFanChart: React.FC<Props> = ({ fanChart, initialCapital, height = 320 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<T.TVL.IChartApi | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth || 800,
      height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#d1d4dc",
      },
      rightPriceScale: { visible: true, borderColor: "#2a2e39" },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#758696", width: 1, style: LineStyle.Dashed },
        horzLine: { color: "#758696", width: 1, style: LineStyle.Dashed },
      },
      grid: {
        vertLines: { color: "rgba(42, 46, 57, 0.6)" },
        horzLines: { color: "rgba(42, 46, 57, 0.6)" },
      },
      timeScale: {
        borderColor: "#2a2e39",
        timeVisible: false,
        secondsVisible: false,
        tickMarkFormatter: (time: number) => `#${time}`,
      },
      localization: {
        timeFormatter: (time: number) => `Trade #${time}`,
        priceFormatter: (price: number) => `$${price.toFixed(2)}`,
      },
    });
    chartRef.current = chart;

    const toTime = (index: number) => index as unknown as T.TVL.Time;

    const startingCapital = chart.addSeries(LineSeries, {
      color: "rgba(255, 255, 255, 0.25)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "Start",
      crosshairMarkerVisible: false,
      priceLineVisible: false,
    });
    startingCapital.setData(
      fanChart.x_trade_index.map((index) => ({ time: toTime(index), value: initialCapital })),
    );

    // Kept high-to-low so the tooltip reads top band first, matching how the fan is stacked.
    const bandInfos: SeriesInfo[] = [];
    for (const [percentile, values] of Object.entries(fanChart.bands)) {
      const style = BAND_STYLE[percentile] ?? FALLBACK_BAND;
      const series = chart.addSeries(LineSeries, {
        title: percentile,
        color: style.color,
        lineWidth: style.width,
        priceLineVisible: false,
        crosshairMarkerRadius: 3,
      });
      series.setData(
        fanChart.x_trade_index.map((index, i) => ({ time: toTime(index), value: values[i] })),
      );
      bandInfos.push({ series, label: percentile, color: style.color });
    }
    bandInfos.sort((a, b) => Number(b.label.slice(1)) - Number(a.label.slice(1)));

    for (const path of fanChart.sample_paths) {
      const series = chart.addSeries(LineSeries, {
        title: `sim ${path.id}`,
        color: "rgba(120, 144, 200, 0.45)",
        lineWidth: 1,
        priceLineVisible: false,
        crosshairMarkerVisible: false,
      });
      series.setData(
        fanChart.x_trade_index.map((index, i) => ({ time: toTime(index), value: path.equity[i] })),
      );
    }

    // A floating readout of every band's equity at the hovered trade. lightweight-charts has no
    // built-in HTML tooltip, so it is driven off the crosshair-move event.
    const tooltip = tooltipRef.current;
    const handleCrosshair = (param: T.TVL.MouseEventParams) => {
      if (!tooltip) return;
      if (!param.point || param.time == null || !param.seriesData.size) {
        tooltip.style.display = "none";
        return;
      }
      const rows = bandInfos
        .map(({ series, label, color }) => {
          const point = param.seriesData.get(series) as { value?: number } | undefined;
          if (point?.value == null) return "";
          return `<div class="McFanTooltipRow"><span class="McFanTooltipDot" style="background:${color}"></span>${label}: $${point.value.toFixed(2)}</div>`;
        })
        .join("");
      tooltip.innerHTML = `<div class="McFanTooltipHead">Trade #${param.time}</div>${rows}`;
      tooltip.style.display = "block";
      const x = Math.min(param.point.x + 14, (containerRef.current?.clientWidth ?? 0) - 140);
      tooltip.style.left = `${Math.max(x, 0)}px`;
      tooltip.style.top = `${param.point.y + 14}px`;
    };
    chart.subscribeCrosshairMove(handleCrosshair);

    chart.timeScale().fitContent();

    // Watch the container, not the window: the chart is created while its tab panel may not yet be
    // at full width, so it can mount narrow. A ResizeObserver catches that first real layout (and
    // any later container change) and widens the chart, instead of waiting on a window resize.
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width && chartRef.current) chartRef.current.applyOptions({ width });
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshair);
      chart.remove();
      chartRef.current = null;
    };
  }, [fanChart, initialCapital, height]);

  return (
    <div className="McFanChartWrap">
      <div className="McFanChartAxisLabel text-body-secondary">Equity ($)</div>
      <div className="McFanChart" ref={containerRef}>
        <div className="McFanTooltip" ref={tooltipRef} />
      </div>
    </div>
  );
};
